import {
  Box,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Radio,
  RadioGroup,
  Select,
  Typography,
} from '@mui/material';
import { Fragment, memo, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createContractSelectionOptions } from 'app/components/Builder/OptionsStrategy/FieldsData';
import MuiButton from 'app/design/uiComponents/MuiButton';
import { Formik } from 'formik';
import * as Yup from 'yup';
import FormattedMessage from 'app/design/uiComponents/FormattedMessage';
import MuiLoadingButton from 'app/design/uiComponents/MuiLoadingButton';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import { SignalIronFlyValueType } from './types';
import MuiToggleButton from 'app/design/uiComponents/MuiToggleButton';
import { SignalProductTypes, SignalPositionTypes } from '../util';

const BotGenerateSignalTemplateIronFly = ({
  niftySymbol,
  handleGenerateSignal,
  getAtmInstrument,
  handleClickCancel,
}: {
  niftySymbol: string;
  handleGenerateSignal: (type: string, values: SignalIronFlyValueType) => void;
  getAtmInstrument: (atmValue: number) => number;
  handleClickCancel: () => void;
}) => {
  const { t } = useTranslation();

  const [val1AtmPrice, setVal1AtmPrice] = useState(0);
  const [val2PEAtmPrice, setVal2PEAtmPrice] = useState(0);
  const [val2CEAtmPrice, setVal2CEAtmPrice] = useState(0);
  const [val3AtmPrice, setVal3AtmPrice] = useState(0);

  const contractSelectionOptions = useMemo(() => {
    return createContractSelectionOptions(niftySymbol, 'strike', 'call');
  }, [niftySymbol]);

  useEffect(() => {
    const difVal1StrikePrice = getAtmInstrument(-5);
    const difVal2StrikePrice = getAtmInstrument(0);
    const difVal3StrikePrice = getAtmInstrument(5);
    setVal1AtmPrice(difVal1StrikePrice ? difVal1StrikePrice : 0);
    setVal2PEAtmPrice(difVal2StrikePrice ? difVal2StrikePrice : 0);
    setVal2CEAtmPrice(difVal2StrikePrice ? difVal2StrikePrice : 0);
    setVal3AtmPrice(difVal3StrikePrice ? difVal3StrikePrice : 0);
  }, [getAtmInstrument]);

  return (
    <>
      <Box sx={{ height: '100%', overflow: 'auto', display: 'grid' }}>
        <Formik
          initialValues={
            {
              type: 'short',
              value1: 5,
              value2: 0,
              value3: 5,
              lots: 1,
              productType: 'MIS',
              orderTag: '',
              tradeId: '',
              positionType: 'EN',
            } as SignalIronFlyValueType
          }
          validationSchema={Yup.object().shape({
            lots: Yup.number()
              .min(1, t('bot_deploy_modal.valid_msg.valid', { type: 'Lots' }))
              .required(t('bot_deploy_modal.valid_msg.req', { type: 'Lots' })),
          })}
          onSubmit={(values, actions) => {
            handleGenerateSignal('IRON_FLY', values);
            actions.resetForm();
          }}
        >
          {({
            values,
            errors,
            setFieldValue,
            handleBlur,
            handleChange,
            touched,
            isValid,
            resetForm,
            submitForm,
          }) => {
            return (
              <>
                <Box
                  component="form"
                  noValidate
                  // onSubmit={handleSubmit}
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    height: '100%',
                    position: 'relative',
                  }}
                >
                  <Box sx={{ mb: 2, flex: 1 }}>
                    <Box sx={{ mb: 3 }}>
                      <FormControl>
                        <RadioGroup
                          row
                          name="type"
                          value={values.type}
                          onChange={e => {
                            const type = e.target.value;
                            setFieldValue('type', type);
                            const newVal1StrikePrice = getAtmInstrument(
                              -1 * values.value1,
                            );
                            setVal1AtmPrice(newVal1StrikePrice);

                            const newVal2PEStrikePrice = getAtmInstrument(
                              -1 * values.value2,
                            );
                            const newVal2CEStrikePrice = getAtmInstrument(
                              values.value2,
                            );
                            setVal2PEAtmPrice(newVal2PEStrikePrice);
                            setVal2CEAtmPrice(newVal2CEStrikePrice);

                            const newVal3StrikePrice = getAtmInstrument(
                              values.value3,
                            );
                            setVal3AtmPrice(newVal3StrikePrice);
                          }}
                        >
                          <FormControlLabel
                            value="short"
                            control={<Radio />}
                            label={`${t('bot_signals.template_form.short')}`}
                          />
                          <FormControlLabel
                            value="long"
                            control={<Radio />}
                            label={`${t('bot_signals.template_form.long')}`}
                          />
                        </RadioGroup>
                      </FormControl>
                    </Box>
                    <Grid container columnSpacing={2}>
                      <Grid item xs={12} md={6} lg={4}>
                        <FormControl fullWidth sx={{ mb: 3 }}>
                          <InputLabel id="label-value1">
                            {`${values.type === 'short' ? '+' : '-'}${t(
                              'bot_signals.template_form.1x',
                            )} [${t(`bot_signals.template_form.pe`)}]`}
                          </InputLabel>
                          <Select
                            labelId="label-value1"
                            name="value1"
                            value={values.value1}
                            label={`${values.type === 'short' ? '+' : '-'}${t(
                              'bot_signals.template_form.1x',
                            )} [${t(`bot_signals.template_form.pe`)}]`}
                            onChange={e => {
                              const val = e.target.value as number;
                              setFieldValue('value1', val);
                              const newStrikePrice = getAtmInstrument(-1 * val);
                              setVal1AtmPrice(newStrikePrice);
                            }}
                          >
                            {contractSelectionOptions.map(interval => (
                              <MenuItem value={interval.key} key={interval.key}>
                                {interval.label}
                              </MenuItem>
                            ))}
                          </Select>
                          <Box className="extra-label">
                            <Typography variant="body2">
                              {val1AtmPrice}
                            </Typography>
                          </Box>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} md={6} lg={4}>
                        <FormControl fullWidth sx={{ mb: 3 }}>
                          <InputLabel id="label-value2">
                            {`${values.type === 'short' ? '-' : '+'}${t(
                              'bot_signals.template_form.1x',
                            )} [${t(`bot_signals.template_form.pe`)}/${t(
                              `bot_signals.template_form.ce`,
                            )}]`}
                          </InputLabel>
                          <Select
                            labelId="label-value2"
                            name="value2"
                            value={values.value2}
                            label={`${values.type === 'short' ? '-' : '+'}${t(
                              'bot_signals.template_form.1x',
                            )} [${t(`bot_signals.template_form.pe`)}/${t(
                              `bot_signals.template_form.ce`,
                            )}]`}
                            onChange={e => {
                              const val = e.target.value as number;
                              setFieldValue('value2', val);
                              const newPEStrikePrice = getAtmInstrument(
                                -1 * val,
                              );
                              const newCEStrikePrice = getAtmInstrument(val);
                              setVal2PEAtmPrice(newPEStrikePrice);
                              setVal2CEAtmPrice(newCEStrikePrice);
                            }}
                          >
                            {contractSelectionOptions.map(interval => (
                              <MenuItem value={interval.key} key={interval.key}>
                                {interval.label}
                              </MenuItem>
                            ))}
                          </Select>
                          <Box className="extra-label">
                            <Box
                              sx={{ display: 'flex', flexDirection: 'column' }}
                            >
                              <Typography variant="body2">
                                PE {val2PEAtmPrice}
                              </Typography>
                              <Typography variant="body2">
                                CE {val2CEAtmPrice}
                              </Typography>
                            </Box>
                          </Box>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} md={6} lg={4}>
                        <FormControl fullWidth sx={{ mb: 3 }}>
                          <InputLabel id="label-value3">
                            {`${values.type === 'short' ? '+' : '-'}${t(
                              'bot_signals.template_form.1x',
                            )} [${t(`bot_signals.template_form.ce`)}]`}
                          </InputLabel>
                          <Select
                            labelId="label-value3"
                            name="value3"
                            value={values.value3}
                            label={`${values.type === 'short' ? '+' : '-'}${t(
                              'bot_signals.template_form.1x',
                            )} [${t(`bot_signals.template_form.ce`)}]`}
                            onChange={e => {
                              const val = e.target.value as number;
                              setFieldValue('value3', val);
                              const newStrikePrice = getAtmInstrument(val);
                              setVal3AtmPrice(newStrikePrice);
                            }}
                          >
                            {contractSelectionOptions.map(interval => (
                              <MenuItem value={interval.key} key={interval.key}>
                                {interval.label}
                              </MenuItem>
                            ))}
                          </Select>
                          <Box className="extra-label">
                            <Typography variant="body2">
                              {val3AtmPrice}
                            </Typography>
                          </Box>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} md={6} lg={4}>
                        <FormControl
                          fullWidth
                          error={Boolean(touched.lots && errors.lots)}
                          variant="outlined"
                          sx={{ mb: 3 }}
                        >
                          <InputLabel htmlFor="outlined-adornment-lots">
                            {t('bot_signals.template_form.lots')}
                          </InputLabel>
                          <OutlinedInput
                            id="outlined-adornment-lots"
                            type="number"
                            name="lots"
                            value={values.lots}
                            onBlur={handleBlur}
                            onChange={handleChange}
                            inputProps={{
                              min: 1,
                              style: { textAlign: 'center' },
                            }}
                            endAdornment={
                              <InputAdornment position="end">
                                <IconButton
                                  onClick={() =>
                                    setFieldValue('lots', values.lots + 1)
                                  }
                                >
                                  <AddIcon />
                                </IconButton>
                              </InputAdornment>
                            }
                            startAdornment={
                              <InputAdornment position="start">
                                <IconButton
                                  onClick={() =>
                                    setFieldValue(
                                      'lots',
                                      values.lots <= 1 ? 1 : values.lots - 1,
                                    )
                                  }
                                >
                                  <RemoveIcon />
                                </IconButton>
                              </InputAdornment>
                            }
                            label={t('bot_signals.template_form.lots')}
                          />
                          {touched.lots && errors.lots && (
                            <FormHelperText error sx={{ mx: 0 }}>
                              {errors.lots}
                            </FormHelperText>
                          )}
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} md={6} lg={4}>
                        <FormControl
                          fullWidth
                          variant="outlined"
                          sx={{ mb: 3 }}
                        >
                          <InputLabel htmlFor="label-tradeId">
                            <FormattedMessage id="bot_signals.form.trade_id_optional" />
                          </InputLabel>
                          <OutlinedInput
                            id="label-tradeId"
                            name="tradeId"
                            value={values.tradeId}
                            onBlur={handleBlur}
                            onChange={handleChange}
                            label={t('bot_signals.form.trade_id_optional')}
                          />
                        </FormControl>
                      </Grid>
                      <Grid item xs={12}></Grid>
                      <Grid item xs={12} md={6} lg={4}>
                        <Box sx={{ mb: 3 }}>
                          <Typography sx={{ mb: 1 }} fontWeight={500}>
                            {t('bot_signals.form.product_type')}
                          </Typography>
                          <Box display="flex" gap={2}>
                            {SignalProductTypes.map(item => (
                              <Fragment key={item.value}>
                                <MuiToggleButton
                                  label={item.label}
                                  value={item.value}
                                  autoWidth={true}
                                  isSelected={item.value === values.productType}
                                  setValue={val => {
                                    setFieldValue('productType', val);
                                  }}
                                />
                              </Fragment>
                            ))}
                          </Box>
                        </Box>
                      </Grid>
                      <Grid item xs={12} md={6} lg={4}>
                        <Box sx={{ mb: 3 }}>
                          <Typography sx={{ mb: 1 }} fontWeight={500}>
                            {t('bot_signals.form.position_type')}
                          </Typography>
                          <Box display="flex" gap={2}>
                            {SignalPositionTypes.map(item => (
                              <Fragment key={item.value}>
                                <MuiToggleButton
                                  label={item.label}
                                  value={item.value}
                                  autoWidth={true}
                                  isSelected={
                                    item.value === values.positionType
                                  }
                                  setValue={val => {
                                    setFieldValue('positionType', val);
                                  }}
                                />
                              </Fragment>
                            ))}
                          </Box>
                        </Box>
                      </Grid>
                    </Grid>
                  </Box>

                  <Box
                    sx={{
                      textAlign: 'center',
                      display: { xs: 'block', md: 'flex' },
                      justifyContent: 'end',
                      flexDirection: 'row-reverse',
                      gap: 2,
                    }}
                  >
                    <MuiLoadingButton
                      // type="submit"
                      type="button"
                      variant="contained"
                      color="primary"
                      disabled={!isValid}
                      // loading={mutation.isLoading}
                      sx={{ mb: 2, width: { xs: '100%', lg: 'auto' } }}
                      onClick={submitForm}
                    >
                      <FormattedMessage id={'buttons.add_basket'} />
                    </MuiLoadingButton>
                    <MuiButton
                      variant="contained"
                      color="secondary"
                      onClick={() => {
                        handleClickCancel();
                        resetForm();
                      }}
                      sx={{ mb: 2, width: { xs: '100%', lg: 'auto' } }}
                    >
                      <FormattedMessage id="buttons.cancel" />
                    </MuiButton>
                  </Box>
                </Box>
              </>
            );
          }}
        </Formik>
      </Box>
    </>
  );
};

export default memo(BotGenerateSignalTemplateIronFly);
